import React, { useEffect, useState } from "react";
import { Box, InputBase, Slider, Typography, useTheme } from "@mui/material";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import {
  boundsToSlider,
  clampToRange,
  withThumbAt,
} from "../../../helper/rangeFilter";
import FilterPanelActions from "./FilterPanelActions";

function SizeInput({
  value,
  onChange,
  onFocus,
  onBlur,
  onKeyDown,
  onUp,
  onDown,
  disabled,
  placeholder,
  shellSx,
}) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const primary = theme.palette.primary.main;
  const inputHeight = shellSx?.height ?? "40px";

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "stretch",
        border: `1px solid ${isDark ? "rgba(255,255,255,0.18)" : "#d0d0d0"}`,
        borderRadius: "8px",
        backgroundColor: isDark ? "#2A2A2A" : "#fff",
        flex: 1,
        minWidth: 0,
        height: "40px",
        overflow: "hidden",
        "&:focus-within": { borderColor: primary },
        ...shellSx,
      }}
    >
      <InputBase
        disabled={disabled}
        value={value}
        placeholder={placeholder}
        onChange={onChange}
        onFocus={onFocus}
        onBlur={onBlur}
        onKeyDown={(e) => {
          onKeyDown?.(e);
        }}
        sx={{
          flex: 1,
          "& input": {
            textAlign: "center",
            fontSize: "13px",
            fontWeight: 400,
            color: isDark ? "#fff" : "#222",
            padding: "0 6px",
            height: inputHeight,
            cursor: disabled ? "default" : "text",
          },
        }}
      />
      <Box sx={{ display: "flex", flexDirection: "column", width: "18px", flexShrink: 0 }}>
        <Box
          onMouseDown={(e) => {
            e.preventDefault();
            if (!disabled) onUp?.();
          }}
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: disabled ? "default" : "pointer",
            color: isDark ? "rgba(255,255,255,0.6)" : "#666",
            "&:hover": !disabled ? { color: primary, bgcolor: `${primary}0F` } : {},
          }}
        >
          <ArrowDropUpIcon sx={{ fontSize: 16 }} />
        </Box>
        <Box
          onMouseDown={(e) => {
            e.preventDefault();
            if (!disabled) onDown?.();
          }}
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: disabled ? "default" : "pointer",
            color: isDark ? "rgba(255,255,255,0.6)" : "#666",
            "&:hover": !disabled ? { color: primary, bgcolor: `${primary}0F` } : {},
          }}
        >
          <ArrowDropDownIcon sx={{ fontSize: 16 }} />
        </Box>
      </Box>
    </Box>
  );
}

/**
 * Numeric range editor used by independent Revenue and Employee filter menus.
 * Preserves the same log-slider + typed input behavior as Version 1 Size filters.
 */
export default function RangeFilterControls({
  title,
  scale,
  lowerBound,
  upperBound,
  setLowerBound,
  setUpperBound,
  disabled = false,
  onClear,
  onApply,
  /** Hide title + helper copy (onboarding already provides question/supporting text). */
  showHeader = true,
  /** Hide Clear / Apply row (onboarding uses the shell Continue instead). */
  showActions = true,
  /** Optional shell styles for min/max inputs (used by onboarding). */
  sizeInputSx,
}) {
  const theme = useTheme();
  const primary = theme.palette.primary.main;
  const [sliderValues, setSliderValues] = useState(() => boundsToSlider(lowerBound, upperBound, scale));
  const [editing, setEditing] = useState([false, false]);
  const [raw, setRaw] = useState(["", ""]);
  const [sliderKey, setSliderKey] = useState(0);

  useEffect(() => {
    setSliderValues(boundsToSlider(lowerBound, upperBound, scale));
    setSliderKey((key) => key + 1);
    setEditing([false, false]);
  }, [lowerBound, upperBound, scale]);

  const commitSlider = (nextSlider) => {
    setSliderValues(nextSlider);
    setLowerBound(scale.fromSlider(nextSlider[0]));
    setUpperBound(scale.fromSlider(nextSlider[1]));
  };

  const handleInputFocus = (idx) => {
    const current = scale.fromSlider(sliderValues[idx]);
    const isRevenueMillions = scale.minPlaceholder === "Min (M)";
    const display = isRevenueMillions
      ? (() => {
          const millions = current / 1_000_000;
          return millions % 1 === 0
            ? String(Math.round(millions))
            : millions.toFixed(2).replace(/\.?0+$/, "");
        })()
      : String(Math.round(current));

    setEditing((prev) => {
      const next = [...prev];
      next[idx] = true;
      return next;
    });
    setRaw((prev) => {
      const next = [...prev];
      next[idx] = display;
      return next;
    });
  };

  const handleInputCommit = (idx) => {
    const parsed = scale.parse(raw[idx]);
    if (!Number.isNaN(parsed) && parsed >= 0) {
      const clamped = clampToRange(parsed, scale.min, scale.max);
      const nextSlider = withThumbAt(sliderValues, idx, scale.toSlider(clamped));
      setSliderValues(nextSlider);
      if (idx === 0) {
        setLowerBound(clamped);
        if (nextSlider[1] !== sliderValues[1]) setUpperBound(clamped);
      } else {
        setUpperBound(clamped);
        if (nextSlider[0] !== sliderValues[0]) setLowerBound(clamped);
      }
    }
    setEditing((prev) => {
      const next = [...prev];
      next[idx] = false;
      return next;
    });
  };

  const handleStep = (idx, direction) => {
    setEditing((prev) => {
      const next = [...prev];
      next[idx] = false;
      return next;
    });
    const current = scale.fromSlider(sliderValues[idx]);
    const stepped = clampToRange(scale.step(current, direction), scale.min, scale.max);
    commitSlider(withThumbAt(sliderValues, idx, scale.toSlider(stepped)));
  };

  const displayValue = (idx) => {
    if (editing[idx]) return raw[idx];
    return scale.format(scale.fromSlider(sliderValues[idx]));
  };

  return (
    <Box sx={{ opacity: disabled ? 0.45 : 1, pointerEvents: disabled ? "none" : "auto" }}>
      {showHeader ? (
        <>
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: 12,
              lineHeight: 1.35,
              mb: 0.5,
              color: theme.palette.mode === "dark" ? "#D0D0D0" : "#58595B",
              display: "flex",
              alignItems: "flex-start",
              flexWrap: "wrap",
              gap: 0.5,
            }}
          >
            {title}
          </Typography>
          <Typography
            sx={{
              fontSize: 12,
              fontWeight: 500,
              color: theme.palette.text.secondary,
              mb: 1,
            }}
          >
            Set a minimum, maximum, or drag both ends of the range.
          </Typography>
        </>
      ) : null}
      <Box sx={{ display: "flex", gap: "8px", alignItems: "center" }}>
        <SizeInput
          disabled={disabled}
          value={displayValue(0)}
          placeholder={scale.minPlaceholder}
          shellSx={sizeInputSx}
          onFocus={() => handleInputFocus(0)}
          onChange={(e) =>
            setRaw((prev) => {
              const next = [...prev];
              next[0] = e.target.value;
              return next;
            })
          }
          onBlur={() => handleInputCommit(0)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              handleInputCommit(0);
              onApply?.();
            }
            if (e.key === "Escape") {
              setEditing((prev) => {
                const next = [...prev];
                next[0] = false;
                return next;
              });
            }
          }}
          onUp={() => handleStep(0, 1)}
          onDown={() => handleStep(0, -1)}
        />
        <Typography sx={{ fontSize: 13, color: "#999", flexShrink: 0, userSelect: "none", px: 0.25 }}>
          –
        </Typography>
        <SizeInput
          disabled={disabled}
          value={displayValue(1)}
          placeholder={scale.maxPlaceholder}
          shellSx={sizeInputSx}
          onFocus={() => handleInputFocus(1)}
          onChange={(e) =>
            setRaw((prev) => {
              const next = [...prev];
              next[1] = e.target.value;
              return next;
            })
          }
          onBlur={() => handleInputCommit(1)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              handleInputCommit(1);
              onApply?.();
            }
            if (e.key === "Escape") {
              setEditing((prev) => {
                const next = [...prev];
                next[1] = false;
                return next;
              });
            }
          }}
          onUp={() => handleStep(1, 1)}
          onDown={() => handleStep(1, -1)}
        />
      </Box>
      <Box sx={{ mt: 2.25, px: 0.75 }}>
        <Slider
          key={sliderKey}
          min={scale.toSlider(scale.min)}
          max={scale.toSlider(scale.max)}
          disabled={disabled}
          value={sliderValues}
          step={0.01}
          onChange={(_event, value) => setSliderValues(value)}
          onChangeCommitted={(_event, value) => commitSlider(value)}
          scale={(x) => scale.fromSlider(x)}
          sx={{
            color: primary,
            height: 6,
            transition: "opacity 160ms ease",
            "& .MuiSlider-thumb": {
              width: 20,
              height: 20,
              transition: "box-shadow 160ms ease, transform 160ms ease",
              boxShadow: `0 2px 6px ${primary}59`,
              "&:hover, &.Mui-focusVisible": {
                boxShadow: `0 0 0 8px ${primary}26`,
                transform: "scale(1.04)",
              },
              "&:active": { boxShadow: `0 0 0 12px ${primary}33` },
            },
            "& .MuiSlider-track": { border: "none", height: 6 },
            "& .MuiSlider-rail": { opacity: 0.22, height: 6 },
          }}
        />
      </Box>
      {showActions ? (
        <FilterPanelActions
          clearDisabled={disabled || (lowerBound == null && upperBound == null)}
          onClear={onClear}
          onApply={onApply}
        />
      ) : null}
    </Box>
  );
}
